import store from '@/store';
import router from '@/router';
import { CLEAR_ERRORMSG, LOGOUT, SYNC_ACCOUNT_ORGANIZATION, UPDATE_ERRORMSG } from '@/store/action_types';
import { AXIOS_TIMEOUT, SERVER_ERR_MSG } from '@/utils/const';
import { resetLoaders } from '@/utils/loaders';
import { MODULE_DISABLED_ERROR_CODE, isModuleEnabled, moduleForRoute } from '@/utils/modules';
import axios, { AxiosInstance } from 'axios';
import { DASHBOARD_ROUTE, LOGIN_ROUTE } from '@/router/routes';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * The caller shows the error itself: no global error toast, and the request rejects with the
     * axios error (read `error.response.data.message`). Sign-out on 401/403 and the module
     * licence refresh still happen.
     */
    silent?: boolean;
  }
}

/**
 * The API refused the call because the organization is not licensed for that
 * module. Refresh the licence list (the admin may have just changed it) and, if
 * the page being viewed belongs to the module, move the user back to the dashboard.
 */
const handleModuleDisabled = (message: string, silent = false) => {
  if (!silent) store.dispatch(UPDATE_ERRORMSG, { errorMsg: message });
  store.dispatch(SYNC_ACCOUNT_ORGANIZATION, { force: true }).then(() => {
    const required = moduleForRoute(router.currentRoute.value.name);
    if (required && !isModuleEnabled(store.getters.account.data?.organization, required)) {
      router.push({ name: DASHBOARD_ROUTE });
    }
  });
};

const baseAPI: AxiosInstance = axios.create({
  baseURL: `${process.env.NODE_ENV == 'development' ? process.env.VUE_APP_DEV_SERVER_URL : process.env.VUE_APP_SERVER_URL}/${process.env.VUE_APP_VERSION}`,
  // baseURL: `${process.env.VUE_APP_SERVER_URL}/${process.env.VUE_APP_VERSION}`,
  headers: {
    'Content-type': 'application/json',
  },
  timeout: AXIOS_TIMEOUT,
});

baseAPI.interceptors.request.use(
  (request) => {
    const token = store.getters.sessionToken;
    if (token) request.headers.Authorization = `Bearer ${token}`;
    return request;
  },
  (requestError) => {
    // TODO: add error handling for when network disappears
    return requestError;
  }
);

/**
 * File downloads (responseType "blob") receive the API's JSON error body as a Blob. Read it back
 * into an object so the handlers below, and the caller, see the real message.
 */
const readBlobErrorBody = async (error: any): Promise<void> => {
  const data = error?.response?.data;
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return;
  try {
    const text = await data.text();
    error.response.data = text ? JSON.parse(text) : {};
  } catch (e) {
    // Not JSON (a proxy error page, for example): keep the body as it came.
  }
};

baseAPI.interceptors.response.use(
  (response) => {
    store.dispatch(CLEAR_ERRORMSG);
    return response;
  },
  async function (error) {
    try {
      console.log('error => ', error);
      console.log('error.code => ', error.code === 'ECONNABORTED');

      // clear error messages
      resetLoaders();

      await readBlobErrorBody(error);

      if (error.config?.skipGlobalErrorHandler) {
        return Promise.reject(error);
      }

      const silent = !!error.config?.silent;
      const showError = (errorMsg: string) => {
        if (!silent) store.dispatch(UPDATE_ERRORMSG, { errorMsg });
      };

      // Do something with response error
      if (error.code === 'ECONNABORTED') {
        showError('Error! Please check your connection. ECONNABORTED.');
      }

      if (error.response?.status === 400) {
        showError(error.response.data.message);
        // store.dispatch(LOGOUT_LOCALLY)
      }

      if (error.response.status === 401) {
        showError(error.response.data.message);
        store.dispatch(LOGOUT);
        router.push({ name: LOGIN_ROUTE });
      }

      if (error.response.status === 403) {
        if (error.response.data?.error?.errorCode === MODULE_DISABLED_ERROR_CODE) {
          handleModuleDisabled(error.response.data.message, silent);
        } else {
          showError(error.response.data.message);
          store.dispatch(LOGOUT);
          router.push({ name: LOGIN_ROUTE });
        }
      }

      if (error.response.status === 404) {
        showError(error.response.data.message);
        // router.push({ name: '404' })
      }

      if (error.response.status === 409) {
        showError(error.response.data.message);
      }

      if (error.response.status === 500) {
        showError(error.response.data.message || SERVER_ERR_MSG);
      }

      if (silent) return Promise.reject(error);
    } catch (e) {
      return Promise.reject(error);
    }
  }
);

export default baseAPI;
